import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  styled,
  Link,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import QRCode from "react-qr-code";
import BtcIcon from "../../assets/btc.svg";
import UsdtIcon from "../../assets/usdt.svg";
import EthIcon from "../../assets/eth.svg";
import UsdcIcon from "../../assets/usdc.svg";
import sendSvg from "../../assets/send.svg";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import logo from "../../assets/xpdlogo.png";
import TransactionDetailsDialog from "./TransactionDetailsDialog";

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#542374',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#542374',
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#542374'
  }
}));

const StyledMenuItem = styled(MenuItem)({
  padding: '10px 16px',
  '&.Mui-selected': {
    backgroundColor: 'rgba(84, 35, 116, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(84, 35, 116, 0.12)'
    }
  }
});

const Dashboard = () => {
  const [cryptoAccounts, setCryptoAccounts] = useState([]);
  const [allAccounts, setallAccounts] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("BTC");
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [groupedAccounts, setGroupedAccounts] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [cardTrasactions, setCardTransactions] = useState([]);

  const [suspendOpen, setSuspendOpen] = useState(true); // For service suspension notice

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = Cookies.get("token");
        const email = Cookies.get("email");

        const response = await axios.get(`/api/api/v1/getListOfWallets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { email },
        });

        const accounts = response.data.map(wallet => ({
          currency: wallet.coin,
          currencyName: wallet.coin,
          symbol: wallet.coin === "BTC" ? "₿" : wallet.coin === "ETH" ? "Ξ" : "₮",
          balance: wallet.available_balance,
          onHold: wallet.pending_balance,
          icon: wallet.coin === "BTC" ? BtcIcon : wallet.coin === "ETH" ? EthIcon : wallet.coin === "USDT" ? UsdtIcon : UsdcIcon,
          address: wallet.asset_address,
          network: wallet.path,
          transactions: wallet.transactions || []
        }));

        // all accounts
        const allAccounts = response.data.map((wallet) => ({
          currency: wallet.coin,
          currencyName: wallet.coin,
          symbol: wallet.coin === "BTC" ? "₿" : wallet.coin === "ETH" ? "Ξ" : "₮",
          balance: wallet.available_balance,
          onHold: wallet.pending_balance,
          icon: wallet.coin === "BTC" ? BtcIcon : wallet.coin === "ETH" ? EthIcon : wallet.coin === "USDT" ? UsdtIcon : wallet.coin === "USDC" ? UsdcIcon : null,
          transactions: wallet.transactions || [],
        }));

        // Group accounts by coin and sum balances
        const groupedAccounts = response.data.reduce((acc, wallet) => {
          const existingAccount = acc.find((a) => a.currency === wallet.coin);

          if (existingAccount) {
            // Sum balances and on-hold amounts for the same coin
            existingAccount.balance += wallet.available_balance;
            existingAccount.onHold += wallet.pending_balance;
          } else {
            // Add a new account for the coin
            acc.push({
              currency: wallet.coin,
              currencyName: wallet.coin,
              symbol: wallet.coin === "BTC" ? "₿" : wallet.coin === "ETH" ? "Ξ" : "₮",
              balance: wallet.available_balance,
              onHold: wallet.pending_balance,
              icon: wallet.coin === "BTC" ? BtcIcon : wallet.coin === "ETH" ? EthIcon : wallet.coin === "USDT" ? UsdtIcon : wallet.coin === "USDC" ? UsdcIcon : null,
              transactions: wallet.transactions || [],
            });
          }

          return acc;
        }, []);
        const coinOrder = ["USDT", "USDC"];

        groupedAccounts.sort((a, b) => {
          const indexA = coinOrder.indexOf(a.currency);
          const indexB = coinOrder.indexOf(b.currency);

          return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
        });

        setGroupedAccounts(groupedAccounts);
        setCryptoAccounts(accounts);
        setallAccounts(allAccounts);
        setSelectedNetwork(accounts.find(acc => acc.currency === selectedCurrency)?.network || null);
      } catch (err) {
        console.error("Error fetching accounts:", err);
      }
    };

    const fetchTransactions = async () => {
      try {
        const token = Cookies.get("token");
        const email = Cookies.get("email");

        const response = await axios.get(`/api/api/v1/getLatestTransactions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { email },
        });

        setTransactions(response.data);
      } catch (err) {
        console.error("Error fetching transactions:", err);
      }
    };
    fetchAccounts();
    fetchTransactions();
  }, [selectedCurrency]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  const handleTransactionClick = (transactionId) => {
    setSelectedTransactionId(transactionId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTransactionId(null);
  };
  const handleAddDialogOpen = (wallet) => {
    setSelectedCurrency(wallet.currency);
    setSelectedNetwork(wallet.network);
    setAddDialogOpen(true);
  };
  const handleAddDialogClose = () => setAddDialogOpen(false);

  const handleTransactionDialogOpen = (transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDialogOpen(true);
  };
  const handleTransactionDialogClose = () => {
    setSelectedTransaction(null);
    setTransactionDialogOpen(false);
  };

  const handleCopyAddress = () => {
    const address = cryptoAccounts.find(
      (acc) => acc.currency === selectedCurrency && (!selectedNetwork || acc.network === selectedNetwork)
    )?.address;
    if (address) {
      navigator.clipboard.writeText(address);
      setMessage("Wallet address copied to clipboard");
      setSnackbarOpen(true);
    }
  };
  const handleTransactionDetailsOpen = (transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDialogOpen(true);
  };
  {/* Helper component for detail rows */ }
  const DetailRow = ({ label, value }) => (
    <Box
      sx={{
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      <Grid container>
        <Grid item xs={3}>
          <Typography
            sx={{
              color: '#542a74',
              fontWeight: 600,
              textTransform: 'capitalize'
            }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}
          >
            {value?.toString() || "N/A"}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
  const handleDownloadPDF = () => {
    if (!selectedTransaction) return;

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const generatePDF = async () => {
      try {
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        let yPos = 20;

        // Function to add header
        const addHeader = async () => {
          const logoImg = await loadImage(logo);
          const logoHeight = 25;
          const logoWidth = (logoImg.width * logoHeight) / logoImg.height;
          const xPos = (pageWidth - logoWidth) / 2;

          const canvas = document.createElement('canvas');
          canvas.width = logoImg.width;
          canvas.height = logoImg.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(logoImg, 0, 0);
          const logoData = canvas.toDataURL('image/png');

          doc.addImage(logoData, 'PNG', xPos, yPos - 15, logoWidth, logoHeight);
          yPos += 30; // Reduced spacing after logo
        };

        const checkAndAddNewPage = async () => {
          if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = 20;
            await addHeader();
            return true;
          }
          return false;
        };

        // Add initial header
        await addHeader();

        // Transaction Receipt number
        doc.setFontSize(9); // Reduced font size
        doc.setTextColor(128, 128, 128);
        doc.text(`Transaction Receipt`, 20, yPos);
        yPos += 15; // Reduced spacing

        // Date & Status section
        await checkAndAddNewPage();
        doc.setFontSize(9); // Reduced font size
        doc.setTextColor(128, 128, 128);
        doc.text("DATE & TIME", 20, yPos);

        doc.setFontSize(10); // Reduced font size
        doc.setTextColor(0, 0, 0);
        const date = new Date(selectedTransaction.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        doc.text(date, 20, yPos + 4); // Reduced spacing

        // Status
        doc.setFontSize(10); // Reduced font size
        const status = selectedTransaction.status === "0x1" ? "Completed" : "Processing";
        const statusColor = selectedTransaction.status === "0x1" ? [84, 42, 116] : [217, 119, 6];
        doc.setTextColor(...statusColor);
        doc.text(status, 170, yPos + 4, { align: 'right' }); // Reduced spacing
        yPos += 15; // Reduced spacing

        // Transaction Details Section
        await checkAndAddNewPage();
        doc.setFontSize(12); // Reduced font size
        doc.setTextColor(84, 42, 116);
        doc.text("Transaction Details", doc.internal.pageSize.width / 2, yPos, { align: 'center' }); // Centered text
        yPos += 12; // Reduced spacing


        // Details content
        for (const [key, value] of Object.entries(selectedTransaction).filter(([k]) => !['timestamp', 'status'].includes(k))) {
          await checkAndAddNewPage();

          doc.setFont("helvetica", "bold");
          doc.setTextColor(84, 42, 116);
          doc.setFontSize(9); // Reduced font size
          doc.text(key.replace(/([A-Z])/g, ' $1').trim(), 20, yPos);

          doc.setFont("courier", "normal");
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9); // Reduced font size

          const maxWidth = 120;
          const valueStr = value?.toString() || "N/A";
          const splitValue = doc.splitTextToSize(valueStr, maxWidth);

          if (yPos + (splitValue.length * 4) > pageHeight - 60) { // Reduced line height
            await checkAndAddNewPage();
          }

          doc.text(splitValue, 70, yPos);
          yPos += Math.max(splitValue.length * 4, 6); // Reduced line height and spacing

          doc.setDrawColor(224, 224, 224);
          doc.line(20, yPos - 2, 190, yPos - 2); // Adjusted line position
          yPos += 4; // Added small spacing after line
        }

        // Save the PDF
        doc.save(`transaction-receipt.pdf`);

      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    };

    generatePDF();
  };
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Alert Notice Section */}
      <Alert
        severity="info"
        sx={{
          borderRadius: 2,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
      {/* notice for temporarily suspned all service */}
        <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 'bold', color: '#542a74' }}>
          Important Notice:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#000000' }}>
          All services are temporarily suspended due to unforeseen circumstances. We apologize for any inconvenience caused and appreciate your understanding.
        </Typography>
        {/* <Typography variant="body2" sx={{ textAlign: 'center', color: '#000000' }}>
          <span style={{ color: '#542a74' }}><strong>BTC/ETH Top-Up:</strong></span> Available Monday to Friday, 11:00 AM to 7:00 PM only (GMT+4).
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#000000' }}>
          <span style={{ color: '#542a74' }}><strong>USDT Top-Up:</strong></span> Available 24/7, 365 days a year.
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#000000' }}>
          For assistance, please contact{' '}
          <Link href="mailto:support@myxpd.app" sx={{ color: '#000000', textDecoration: 'none' }}>
            support@myxpd.app
          </Link>
        </Typography> */}

      </Alert>

      <Dialog
  open={suspendOpen}
  onClose={() => setSuspendOpen(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 2,
      boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
      p: { xs: 2, sm: 3 },
    },
  }}
>
  <DialogContent sx={{ textAlign: 'center' }}>
    <Box
      sx={{
        backgroundColor: 'rgb(246, 239, 255)',
        p: { xs: 3, sm: 4 },
        borderRadius: 2,
      }}
    >
      <Box sx={{ fontSize: 22, fontWeight: 700, color: 'black', mb: 2 }}>
        Service Notice
      </Box>
      <Box sx={{ fontSize: 16, color: 'black', lineHeight: 1.6 }}>
        All services are temporarily suspended due to unforeseen circumstances.
        We apologize for any inconvenience caused and appreciate your understanding.
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={() => setSuspendOpen(false)}
          sx={{ textTransform: 'none', borderRadius: 2, backgroundColor: '#542a74' }}
        >
          OK
        </Button>
      </Box>
    </Box>
  </DialogContent>
</Dialog>


      <Card
        sx={{
          // marginRight: { xs: 0, sm: 2, md: 4 },
          border: 0,
          borderRadius: 4,
          boxShadow: 0,
          width: "100%",
          mt: 2,
          overflow: "hidden",
        }}
      >

        <CardContent>
          <Typography variant="body1" fontWeight="bold" gutterBottom color="#7517a7" >
            Your Wallets
          </Typography>
          {groupedAccounts.length > 0 ? (
            <Grid
              container
              spacing={2}
              sx={{
                width: '100%',
                margin: 0 // Removes default margin
              }}
            >
              {groupedAccounts.map((account) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={account.currency}
                  sx={{
                    p: { xs: 1, sm: 1.5, md: 2 }, // Responsive padding
                    width: '100%'
                  }}
                >
                  <Box
                    onClick={() => navigate(`/wallets?tab=${account.currency}`)}
                    sx={{
                      cursor: "pointer",
                      width: '100%',
                      "&:hover .card": {
                        borderColor: "#542a74",
                      },
                    }}
                  >
                    <Card
                      className="card"
                      sx={{
                        width: '100%', // Full width on all screens
                        minHeight: { xs: 130, sm: 140, md: 146 },
                        border: "1px solid #ccc",
                        backgroundColor: account.currency === "BTC"
                          ? "#f7931a30"
                          : account.currency === "ETH"
                            ? "#79767240"
                            : account.currency === "USDC"
                              ? "#2775ca30"
                              : account.currency === "USDT"
                                ? "#50af9530"
                                : "#ccc",
                        borderRadius: 4,
                        boxShadow: 0,
                        transition: "border-color 0.3s ease",
                        margin: { xs: 0 }, // Remove margin on mobile
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            ml: 1,
                          }}
                        >
                          <img
                            src={account.icon}
                            alt={`${account.currency} icon`}
                            style={{ width: 35, height: 35 }}
                          />
                        </Box>

                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2" sx={{ color: "#8f8f8f" }}>
                            {account.currencyName}
                          </Typography>
                        </Box>

                        {(account.currency === "BTCS") ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 2,
                            }}
                          >
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  ml: 1,
                                  alignItems: "center",
                                }}
                              >
                                <Typography variant="body1"
                                  sx={{
                                    fontSize: 14,
                                    color: "#8f8f8f",
                                  }}
                                >
                                  Available Soon
                                </Typography>

                              </Box>
                            </Box>
                            <IconButton
                              size="medium"
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: '2.5rem' // Even bigger icon (40px)
                                }
                              }}
                            >
                              <QrCodeScannerIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 2,
                            }}
                          >
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  ml: 1,
                                  alignItems: "center",
                                }}
                              >
                                <Typography variant="body1">
                                  {account.symbol}
                                </Typography>
                                <Box sx={{ ml: 1 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: 24,
                                      fontWeight: "bold",
                                      display: "inline",
                                    }}
                                  >
                                    {Math.floor(account.balance)}
                                    {/* Integer part */}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: "normal",
                                      display: "inline",
                                    }}
                                  >
                                    .
                                    {(account.balance % 1).toFixed(4).split(".")[1]}
                                    {/* Fractional part */}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <IconButton
                              size="medium"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleAddDialogOpen(account);
                              }}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: '2.5rem' // Even bigger icon (40px)
                                }
                              }}
                            >
                              <QrCodeScannerIcon />
                            </IconButton>
                          </Box>

                        )}

                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2 }}
            >
              Your account currently does not have any wallets.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card
        sx={{
          border: 0,
          borderRadius: 4,
          boxShadow: 0,
          width: "100%",
          mt: 2,
          overflow: "hidden"
        }}
      >
        <CardContent>
          <Box>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ mb: 1 }}
              fontWeight="bold"
              color="#7517a7"
            >
              Quick Actions
            </Typography>
            <Grid
              container
              spacing={2}
              sx={{
                width: '100%',
                margin: 0
              }}
            >
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                sx={{
                  p: { xs: 1, sm: 1.5, md: 2 }
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    minHeight: { xs: 130, sm: 140, md: 146 },
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    boxShadow: 0,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#542a74",
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        ml: 1,
                      }}
                    >
                      <img
                        src={sendSvg}
                        alt="Send Crypto"
                        style={{ width: 24, height: 24 }}
                      />
                    </Box>

                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Send Crypto
                      </Typography>
                    </Box>

                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2">
                        Send crypto to another crypto wallets.
                      </Typography>
                    </Box>


                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions Section */}
      <Card sx={{ borderRadius: 4, boxShadow: 0, mt: 2 }}>
        <CardContent>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2  // margin bottom for spacing
          }}>
            <Typography variant="body1" fontWeight="bold" color="#7517a7">
              Recent Transactions
            </Typography>
            <Link
              href="/transactions"  // Replace with your transaction page route
              // component={RouterLink}  // Assuming you're using React Router
              sx={{
                color: '#7517a7',
                textDecoration: 'none',
                fontSize: '14px',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              See more
            </Link>
          </Box>
          <Box sx={{ overflowX: "auto", borderRadius: 4 }}>
            {transactions.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  borderRadius: "8px",
                  overflow: "hidden",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5", textAlign: "left" }}>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      Date/Time
                    </th>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      Coin
                    </th>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      wallet Address
                    </th>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      Amount
                    </th>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      Status
                    </th>
                    <th style={{ padding: "12px", fontWeight: "bold" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 5).map((tx, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        {tx.timestamp ? (
                          new Date(tx.timestamp).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {tx.coin || "N/A"}
                      </td>

                      <td style={{ padding: "12px" }}>{tx.wallet_address || "N/A"}</td>
                      <td style={{ padding: "12px" }}>{tx.amount || "N/A"} {tx.coin || "N/A"}</td>
                      <td style={{ padding: "12px" }}>
                        {tx.status === "0x1" ? (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "green",
                              fontWeight: "bold",
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                backgroundColor: "green",
                                borderRadius: "50%",
                                display: "inline-block",
                                marginRight: 8,
                              }}
                            ></span>
                            Completed
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "orange",
                              fontWeight: "bold",
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                backgroundColor: "orange",
                                borderRadius: "50%",
                                display: "inline-block",
                                marginRight: 8,
                              }}
                            ></span>
                            Processing
                          </Typography>
                        )}
                      </td>

                      <td style={{ padding: "12px" }}>
                        <IconButton
                          aria-label="details"
                          size="small"
                          onClick={() => handleTransactionClick(tx.id)}
                        >
                          <ArrowForwardIosIcon fontSize="inherit" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 2 }}
              >
                No transactions found
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Add Funds Dialog */}
      <Dialog open={addDialogOpen} onClose={handleAddDialogClose}>
        <DialogTitle>Add Funds</DialogTitle>
        <DialogContent>
          <StyledFormControl fullWidth margin="normal">
            <InputLabel>Coin</InputLabel>
            <Select
              value={selectedCurrency}
              onChange={(e) => {
                setSelectedCurrency(e.target.value);
                setSelectedNetwork(null);
              }}
              label="Coin"
            >
              {[...new Set(cryptoAccounts.map((acc) => acc.currency))].map((currency) => (
                <StyledMenuItem key={currency} value={currency}>{currency}</StyledMenuItem>
              ))}
            </Select>
          </StyledFormControl>

          {cryptoAccounts.filter((acc) => acc.currency === selectedCurrency).length > 1 && (
            <StyledFormControl fullWidth margin="normal">
              <InputLabel>Network</InputLabel>
              <Select
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
                label="Network"
              >
                {cryptoAccounts.filter((acc) => acc.currency === selectedCurrency).map((acc) => (
                  <StyledMenuItem key={acc.network} value={acc.network}>{acc.network}</StyledMenuItem>
                ))}
              </Select>
            </StyledFormControl>
          )}

          <Typography variant="body2" gutterBottom>Wallet Address:</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              width: '100%',
            }}
          >
            {selectedCurrency === "BTCS" ? (
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{
                  color: "#8f8f8f",
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Available Soon
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{
                      wordBreak: { xs: 'break-all', sm: 'normal' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {cryptoAccounts.find(
                      (acc) =>
                        acc.currency === selectedCurrency &&
                        (!selectedNetwork || acc.network === selectedNetwork)
                    )?.address || "N/A"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyAddress}
                  sx={{
                    minWidth: { xs: '100%', sm: 'auto' },
                    mt: { xs: 1, sm: 0 },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Copy
                </Button>
              </>
            )}
          </Box>

          {selectedCurrency !== "BTC" && selectedCurrency !== "ETH" && (
            <Box display="flex" justifyContent="center" mt={2}>
              <QRCode
                value={cryptoAccounts.find(
                  (acc) =>
                    acc.currency === selectedCurrency &&
                    (!selectedNetwork || acc.network === selectedNetwork)
                )?.address || ""}
                size={150}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <TransactionDetailsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        transactionId={selectedTransactionId}
      />




      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setSnackbarOpen(false)}>{message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default Dashboard;
